import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AggregateApi, ObjectiveWorkflowAggregate } from '@/api/models/aggregate'
import { FilterDrawerType, IFilter } from '@/components/CourseFiltersDrawer/types'
import { TILE_COLORS } from '@/constants/common'
import { RootState } from '@/store'
import { aggregatesApi } from '../api/aggregatesApi'

export interface AggregatesSliceState {
  backgroundMap: Record<string, string>
  enrolledCoursesSearchText: string
  catalogCoursesSearchText: string
  termIds: string[]
  filters: Record<FilterDrawerType, IFilter>
}

const emptyId = '00000000-0000-0000-0000-000000000000'

const initialState: AggregatesSliceState = {
  backgroundMap: {},
  enrolledCoursesSearchText: '',
  catalogCoursesSearchText: '',
  termIds: [],
  filters: {
    catalog: { courseProgress: [], coursePriority: [], term: [], collection: [] },
    enrolled: { courseProgress: [], coursePriority: [], term: [], collection: [] },
    public: { courseProgress: [], coursePriority: [], term: [], collection: [] },
  },
}

export const fetchTerms = createAsyncThunk<ObjectiveWorkflowAggregate[], { query: Record<string, any> }>(
  'fetchTerms/fetch',
  async ({ query }) => {
    const response = await AggregateApi.getObjectiveWorkflowAggregatesWithQuery(query)
    return response
  },
)

const aggregatesSlice = createSlice({
  name: 'aggregates',
  initialState,
  reducers: {
    resetAggregates(state) {
      // We want to keep the filters, catalogCoursesSearchText, enrolledCoursesSearchText and backgroundMap
      const { filters, catalogCoursesSearchText, enrolledCoursesSearchText, backgroundMap, ...rest } = initialState
      Object.assign(state, rest)
    },
    setEnrolledCoursesSearchText(state, action: PayloadAction<string>) {
      state.enrolledCoursesSearchText = action.payload
    },
    setCatalogCoursesSearchText(state, action: PayloadAction<string>) {
      state.catalogCoursesSearchText = action.payload
    },
    setFilters(state, action: PayloadAction<{ type: FilterDrawerType; filters: IFilter }>) {
      const { type, filters } = action.payload
      state.filters[type] = filters
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(aggregatesApi.endpoints.queryObjectiveAccessesForTerms.matchFulfilled, (state, action) => {
      const objectiveAccesses = action.payload.data

      state.termIds = Array.from(
        new Set(objectiveAccesses.map((oc) => oc?.termId).filter((termId) => termId && termId !== emptyId)),
      ) as string[]
    })

    builder.addMatcher(aggregatesApi.endpoints.queryObjectiveAccesses.matchFulfilled, (state, action) => {
      const aggregates = action.payload.data

      state.backgroundMap = aggregates.reduce((backgroundMap, aggregate, index) => {
        if (!aggregate.objective.imageUrl) {
          backgroundMap[aggregate.id] = TILE_COLORS[index % TILE_COLORS.length]
        }
        return backgroundMap
      }, state.backgroundMap)
    })
  },
})

export const createFiltersSelector = createSelector(
  (state: RootState) => state.aggregates.filters,
  (state: RootState, type: FilterDrawerType) => type,
  (filters, type) => filters[type],
)

export const { resetAggregates, setEnrolledCoursesSearchText, setFilters, setCatalogCoursesSearchText } =
  aggregatesSlice.actions

export default aggregatesSlice.reducer
